import { useEffect, useRef, useState } from "react";
import { useApiOperation } from "src/api/hooks";
import { deviceControlExcuteAsync, getBuildingDeviceControlAsync } from "src/api/iot/iot-api";
import BaseRichTooltip from "src/components/BaseRichTooltip";
import useIntersectionObserver from "src/hooks/useIntersectionObserver";
import { PagePath } from "src/pages/product/details";
import { roundFloat } from "src/utils/common-util";
import { deviceStatusCheck, isPassExecute } from "../monitoring-func";
import { requestWithRetry } from "../monitoring-request";
import { DeviceControlType, ExecuteResultType } from "../monitoring-types";
import { getUnit, monitoringTooltipData } from "../MonitoringTooltopData";

type Props = {
  device: ExecuteResultType;
  buildingId: string;
  partnerId: string;
  idx: number;
};

const isValidJSONString = (value: string) => {
  try {
    JSON.parse(value);
    return true;
  } catch (e) {
    return false;
  }
};

const IoTDeviceComp = ({ device, buildingId, partnerId, idx }: Props) => {
  const target = useRef(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const [localDevice, setLocalDevice] = useState<ExecuteResultType>(device);
  // 취소와 재시도를 판정하려면 실패가 예외로 전파돼야 한다
  const { executeAsync: getIotControl } = useApiOperation(getBuildingDeviceControlAsync, { noErrorModal: true, noLoading: true }); // iot 디바이스 제어 목록
  const { executeAsync: executeDevice } = useApiOperation(deviceControlExcuteAsync, { noErrorModal: true, noLoading: true }); // iot 디바이스 제어 실행

  // 기존 요청 취소 함수
  const abortPendingRequests = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
  };

  // 이전 요청을 취소하고 이번 조회가 쓸 신호를 만든다. 컨트롤러는 컴포넌트당 하나만 둔다
  const startNewRequests = () => {
    abortPendingRequests();
    abortControllerRef.current = new AbortController();

    return abortControllerRef.current.signal;
  };

  const { observe } = useIntersectionObserver(
    async () => {
      await getControlDevicesAndExecute(device, startNewRequests());
    },
    {
      threshold: 0.5,
    },
  );

  // 제어 목록 실행 api
  const executeControlDevice = async (controlDevices: DeviceControlType[], signal: AbortSignal) => {
    const execute = async (item: DeviceControlType) => {
      if (!isPassExecute(item)) return;

      const outcome = await requestWithRetry(() =>
        executeDevice({
          deviceType: "building",
          deviceId: item.buildingDeviceId,
          controlId: item.control.id,
          controlMethod: item.control.method,
          arguments: "",
          cacheable: true,
          signal,
        }),
      );

      // 취소는 화면에서 벗어났거나 새 조회가 시작된 것이라 그대로 둔다
      if (outcome.status === "canceled") return;
      if (outcome.status === "failed") {
        console.error(`최종 실패: deviceId ${item.buildingDeviceId} - ${outcome.error}`);
        return;
      }

      const { data } = outcome.value;
      let parsedResult = isValidJSONString(data.data.result) ? JSON.parse(data.data.result) : data.data.result;
      const isIndoorUnit = item.labelType === "실내기" && item.platform === "B_IOT";
      if (isIndoorUnit && parsedResult.controlPoint) {
        parsedResult = {
          ...parsedResult,
          controlPoint: {
            ...parsedResult.controlPoint,
            label: item.labels?.[0]?.value || "",
          },
        };
      }

      return { ...item, result: parsedResult };
    };

    try {
      const allPromiseResults = await Promise.all(controlDevices.map((item) => execute(item)));

      // 병합 로직 시작
      const mergeMap = new Map();
      allPromiseResults.forEach((item) => {
        if (!item) return;
        const key = `${item.buildingDeviceId}-${item.labelType}`;
        if (!mergeMap.has(key)) {
          mergeMap.set(key, { ...item, result: [item.result] });
        } else {
          const existingItem = mergeMap.get(key);
          existingItem.result.push(item.result);
        }
      });

      // 병합된 result를 하나의 객체로 합치기
      const merged = Array.from(mergeMap.values()).map((item) => ({
        ...item,
        result: Object.assign({}, ...item.result),
      }));

      // setLocalDevice에는 병합된 result만 반영
      if (merged.length > 0) {
        setLocalDevice((prev) => ({
          ...prev,
          isLoading: false,
          result: merged[0].result, // IoTDeviceComp는 단일 디바이스만 표시하므로 첫 번째만 사용
        }));
      } else {
        setLocalDevice((prev) => ({ ...prev, isLoading: false }));
      }
    } catch (error: any) {
      setLocalDevice((prev) => ({ ...prev, isLoading: false }));
      console.error("executeControlDevice 전체 실패:", error);
    }
  };

  // 제어 목록 조회 api
  const getControlDevicesAndExecute = async (controlDevice: ExecuteResultType, signal: AbortSignal) => {
    const { buildingDeviceId, labelType, location, platform, internalDisplayName, isLoading } = controlDevice;

    const outcome = await requestWithRetry(() =>
      getIotControl({
        id: buildingDeviceId,
        deviceType: "building",
        signal,
      }),
    );

    if (outcome.status === "canceled") return;
    if (outcome.status === "failed") {
      console.error(`contorl 호출 최종 실패: deviceId ${buildingDeviceId} - ${outcome.error}`);
      // 에러 시에도 로딩 상태는 종료
      setLocalDevice((prev) => ({ ...prev, isLoading: false }));
      return;
    }

    try {
      const result = outcome.value.data.data.controls.map((control) => ({
        ...control,
        labelType,
        location,
        platform,
        internalDisplayName,
        isLoading,
      }));
      const executeList = result
        .filter(Boolean)
        .flat()
        .filter((item) => item?.control.method === "READ_STATUS");

      await executeControlDevice(executeList as DeviceControlType[], signal);
    } catch (error) {
      // 200 인데 응답 형태가 예상과 다른 경우. 화면을 멈추지 않도록 로그만 남긴다
      console.error(`contorl 응답 처리 실패: deviceId ${buildingDeviceId} - ${error}`);
      setLocalDevice((prev) => ({ ...prev, isLoading: false }));
    }
  };

  useEffect(() => {
    // 컴포넌트 마운트 또는 업데이트 시 이전 요청 취소 후 새로운 요청 시작
    abortPendingRequests();
    observe(target.current);

    // 컴포넌트 언마운트 시 진행 중인 요청 취소
    return () => {
      abortPendingRequests();
    };
  }, [device, buildingId]); // device나 buildingId가 변경되면 재실행

  // 기기 유형에 따라 다른 아이콘 결정
  const renderTouchIcon = () => {
    if (localDevice?.isLoading) {
      return (
        <p style={{ height: 20 }} className="flex-center">
          -
        </p>
      );
    }

    const status = deviceStatusCheck("IOT", localDevice);

    // 각 기기 유형별 아이콘 처리
    switch (localDevice.labelType) {
      case "남자화장실":
        return (
          <p
            className={`flex-center ${
              status === "ACTIVE"
                ? "ic-man-b"
                : status === "INACTIVE"
                ? "ic-man-g"
                : status === "CAUTION"
                ? "circle__chip --caution"
                : "circle__chip --cannot-use"
            }`}
          ></p>
        );
      case "여자화장실":
        return (
          <p
            className={`flex-center ${
              status === "ACTIVE"
                ? "ic-woman-b"
                : status === "INACTIVE"
                ? "ic-woman-g"
                : status === "CAUTION"
                ? "circle__chip --caution"
                : "circle__chip --cannot-use"
            }`}
          ></p>
        );
      case "조명":
      case "실내기":
        return "CIRCLE";
      case "재실":
        return (
          <p
            className={`${
              status === "ACTIVE"
                ? "ic-man-b"
                : status === "INACTIVE"
                ? "ic-man-g"
                : status === "CAUTION"
                ? "circle__chip --caution"
                : "circle__chip --cannot-use"
            } `}
          ></p>
        );
      default:
        return "CIRCLE";
    }
  };

  // 기기 유형에 따라 다른 rightLabel 결정
  const renderRightLabel = () => {
    if (localDevice.labelType === "실내기") {
      if (localDevice.platform === "ST") {
        return (
          <p className="font14">
            {localDevice.result?.switch?.value === "on" && (
              <>
                <span className="mr3">
                  {(localDevice.result?.airConditionerMode && getUnit(localDevice.result?.airConditionerMode?.value)) || ""}
                </span>
                <span className="mr3">
                  {(localDevice.result?.coolingSetpoint && `${roundFloat(localDevice.result?.coolingSetpoint?.value)}°C`) || ""}
                </span>
              </>
            )}
          </p>
        );
      } else {
        return (
          <p className="font14">
            {localDevice.result?.operation?.power === "On" && (
              <>
                <span className="mr3">{(localDevice.result?.mode && getUnit(localDevice.result?.mode?.mode)) || ""}</span>
                <span className="mr3">{(localDevice.result?.temperature && `${roundFloat(localDevice.result.temperature?.desired)}°C`) || ""}</span>
              </>
            )}
          </p>
        );
      }
    }

    return <p className="font14"></p>;
  };

  return (
    <div key={`floor-${localDevice?.labelType}-${idx}`} className="" ref={target}>
      {localDevice && (
        <BaseRichTooltip
          touchIcon={renderTouchIcon()}
          leftLabel={
            <div className="flex-center">
              {localDevice.labelType !== "재실" && localDevice.labelType !== "실내기" && localDevice.labelType !== "조명" && (
                <p className="font14 text-center" style={{ width: 20 }}>
                  {idx + 1}
                </p>
              )}
            </div>
          }
          rightLabel={renderRightLabel()}
          status={deviceStatusCheck("IOT", localDevice)}
          children={monitoringTooltipData({ iot: localDevice }, deviceStatusCheck("IOT", localDevice) === "CAUTION")}
          firstActionLabel="상세보기"
          firstActionFunction={() => {
            window.open(
              PagePath.building.detail.replace(":id", String(buildingId)).replace(":partnerId", String(partnerId)) +
                `?tab=iotDevice&tabType=detail&tabId=${localDevice?.buildingDeviceId}`,
            );
          }}
        />
      )}
    </div>
  );
};

export default IoTDeviceComp;
